import { type ReactNode, useMemo } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import { canAccessAdminRoute, getStoredUserRole } from '@/lib/staff-roles'
import { REPUTATION_INBOX_ENABLED } from '@/lib/reputationInbox'
import { useEdition } from '@/hooks/useEdition'
import { cn } from '@/lib/utils'

interface MarketingShellProps {
  title: string
  description?: string
  /** Optional right-aligned page action (e.g. a date-range picker) rendered
   *  next to the heading on the same row, mirroring the Reports shell so the
   *  two pages share a consistent layout. */
  actions?: ReactNode
  children: ReactNode
}

interface MarketingTab {
  id: string
  label: string
  href: string
}

interface MarketingGroup {
  id: string
  label: string
  tabs: MarketingTab[]
}

// F-MKT-N1: fifteen flat tabs read as a wall of tools, so they sit in six
// groups. The top row is the group; a quiet second row lists the tools inside
// it. Every href is unchanged, so deep links and role rules still apply.
const GROUPS: MarketingGroup[] = [
  {
    id: 'overview',
    label: 'Overview',
    tabs: [
      { id: 'overview', label: 'Overview', href: '/admin/marketing/overview' },
      { id: 'performance', label: 'Performance', href: '/admin/marketing/performance' },
    ],
  },
  {
    id: 'plan',
    label: 'Plan',
    tabs: [
      { id: 'calendar', label: 'Calendar', href: '/admin/marketing/calendar' },
      { id: 'kits', label: 'Kits', href: '/admin/marketing/kits' },
    ],
  },
  {
    id: 'reach',
    label: 'Reach',
    tabs: [
      { id: 'campaigns', label: 'Campaigns', href: '/admin/marketing/campaigns' },
      { id: 'automations', label: 'Automations', href: '/admin/marketing/automations' },
      { id: 'segments', label: 'Segments', href: '/admin/marketing/segments' },
    ],
  },
  {
    id: 'rewards',
    label: 'Rewards',
    tabs: [
      { id: 'loyalty', label: 'Loyalty program', href: '/admin/marketing/loyalty' },
      { id: 'enrolment', label: 'Enrolment', href: '/admin/marketing/enrolment' },
      { id: 'members', label: 'Members', href: '/admin/marketing/members' },
      { id: 'promo-codes', label: 'Promo codes', href: '/admin/marketing/promo-codes' },
      { id: 'gift-cards', label: 'Gift cards', href: '/admin/marketing/gift-cards' },
      { id: 'card-promos', label: 'Bank cards', href: '/admin/marketing/card-promos' },
    ],
  },
  {
    id: 'reviews',
    label: 'Reviews',
    tabs: [
      { id: 'reviews', label: 'Reviews', href: '/admin/marketing/reviews' },
      // Hidden while Google Business Profile API access is pending
      // (lib/reputationInbox.ts).
      ...(REPUTATION_INBOX_ENABLED
        ? [{ id: 'reputation', label: 'Reputation', href: '/admin/marketing/reputation' }]
        : []),
      { id: 'recovery', label: 'Guest recovery', href: '/admin/marketing/recovery' },
    ],
  },
  {
    id: 'print',
    label: 'Print & QR',
    tabs: [
      { id: 'posters', label: 'Posters', href: '/admin/marketing/posters' },
      { id: 'qr-codes', label: 'QR codes', href: '/admin/marketing/qr-codes' },
    ],
  },
]

function isActiveHref(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(href + '/')
}

export function MarketingShell({ title, description, actions, children }: MarketingShellProps) {
  const location = useLocation()
  const { posEnabled } = useEdition()

  // Marketing is admin-only except for Bank cards, which managers own too
  // (staff-roles.ts). Filtering here rather than rendering all eight tabs
  // keeps a manager from clicking straight into a route guard bounce.
  // Bank card promos are a POS-checkout feature, so the tab goes too on
  // marketing-edition deploys (the path is POS-only in staff-roles.ts).
  const groups = useMemo(() => {
    const role = getStoredUserRole()
    const visible = (t: MarketingTab) =>
      (posEnabled || t.id !== 'card-promos') &&
      (role === 'admin' || role === '' || canAccessAdminRoute(role, t.href))
    return GROUPS.map((g) => ({ ...g, tabs: g.tabs.filter(visible) })).filter((g) => g.tabs.length > 0)
  }, [posEnabled])

  const tabCount = groups.reduce((n, g) => n + g.tabs.length, 0)
  const activeGroup =
    groups.find((g) => g.tabs.some((t) => isActiveHref(location.pathname, t.href))) ?? groups[0]

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
          {description && (
            <p className="mt-1 text-muted-foreground max-w-3xl">{description}</p>
          )}
        </div>
        {actions && <div className="lg:pt-1">{actions}</div>}
      </div>

      {/* A one-tab row is noise, not navigation (the manager case). On
          marketing-edition deploys the sidebar lists every tool directly,
          so the rows would be a duplicate nav — hidden entirely there. */}
      {posEnabled && tabCount > 1 && (
        <div className="space-y-3">
          {groups.length > 1 && (
            <div className="flex flex-wrap gap-1 rounded-lg bg-muted/60 p-1" role="navigation" aria-label="Marketing sections">
              {groups.map((g) => {
                const active = g.id === activeGroup?.id
                return (
                  <Link
                    key={g.id}
                    to={g.tabs[0].href}
                    className={cn(
                      'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
                      active
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {g.label}
                  </Link>
                )
              })}
            </div>
          )}
          {activeGroup && activeGroup.tabs.length > 1 && (
            <div className="flex flex-wrap gap-x-5 gap-y-1 border-b" role="navigation" aria-label={`${activeGroup.label} tools`}>
              {activeGroup.tabs.map((t) => {
                const active = isActiveHref(location.pathname, t.href)
                return (
                  <Link
                    key={t.id}
                    to={t.href}
                    className={cn(
                      '-mb-px border-b-2 pb-2 text-sm font-medium transition-colors',
                      active
                        ? 'border-foreground text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {t.label}
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      )}

      {children}
    </div>
  )
}
